import {
  MeiliSearch,
  type Index,
  type Task,
  type EnqueuedTask,
  type SearchResponse,
  type Settings,
  type Synonyms,
} from "meilisearch";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import type { Operator } from "@/src/types/components/operators/Operator";
import type { NewsItem, SlotItem, SearchOptions, MeiliStats } from "@/src/types/components/MeiliSearch";

const client = new MeiliSearch({
  host: import.meta.env.VITE_MEILISEARCH_HOST,
  apiKey: import.meta.env.VITE_MEILISEARCH_API_KEY,
});

const { searchClient } = instantMeiliSearch(
  import.meta.env.VITE_MEILISEARCH_HOST,
  import.meta.env.VITE_MEILISEARCH_API_KEY,
  {
    primaryKey: "id",
    keepZeroFacets: true,
  },
);

const operatorsIndex: Index<Operator> = client.index("operators");
const newsIndex: Index<NewsItem> = client.index("news");
const slotsIndex: Index<SlotItem> = client.index("slots");

const uploadOperators = async (
  operators: Operator[],
  swedishOnly: boolean = true,
  projectKey?: string,
  market?: string,
): Promise<EnqueuedTask> => {
  try {
    let filteredOperators = swedishOnly
      ? operators.filter(
          (operator) => operator.countries && Array.isArray(operator.countries) && operator.countries.includes("se"),
        )
      : operators;

    // Filter out hidden or closed operators
    if (projectKey && market) {
      filteredOperators = filteredOperators.filter((operator) => {
        const isHidden = operator?.rating?.[projectKey]?.[market]?.hide || false;
        const isClosed = operator?.rating?.[projectKey]?.[market]?.closed || false;
        return !isHidden && !isClosed;
      });
    }

    const transformedOperators = filteredOperators.map((operator) => ({
      id: operator.id,
      name: operator.name,
      name_lower: operator.name_lower,
      url: operator.url,
      founded: operator.founded,
      bonus: operator.bonus,
      data: operator.data,
      owner: operator.owner,
      countries: operator.countries,
      languages: operator.languages,
      visuals: operator.visuals,
      logo: operator.logo,
    }));

    // Delete the index before syncing
    console.log(`Clearing operators index before uploading ${transformedOperators.length} filtered operators`);
    await operatorsIndex.deleteAllDocuments();

    // Only add the filtered operators
    return await operatorsIndex.addDocuments(transformedOperators);
  } catch (error) {
    console.error("Error uploading operators to Meilisearch:", error);
    throw error;
  }
};

const searchOperators = async (query: string, options: SearchOptions = {}): Promise<SearchResponse<Operator>> => {
  try {
    return await operatorsIndex.search(query, options);
  } catch (error) {
    console.error("Error searching operators in Meilisearch:", error);
    throw error;
  }
};

const deleteOperator = async (id: number | string): Promise<EnqueuedTask> => {
  try {
    return await operatorsIndex.deleteDocument(id);
  } catch (error) {
    console.error("Error deleting operator from Meilisearch:", error);
    throw error;
  }
};

const updateOperator = async (operator: Operator): Promise<EnqueuedTask> => {
  try {
    const transformedOperator = {
      id: operator.id,
      name: operator.name,
      name_lower: operator.name_lower,
      url: operator.url,
      founded: operator.founded,
      owner: operator.owner,
      countries: operator.countries,
      languages: operator.languages,
      visuals: operator.visuals,
    };
    return await operatorsIndex.updateDocuments([transformedOperator]);
  } catch (error) {
    console.error("Error updating operator in Meilisearch:", error);
    throw error;
  }
};

const uploadNews = async (newsItems: NewsItem[]): Promise<EnqueuedTask | { taskUid: null; message: string }> => {
  try {
    if (!newsItems || newsItems.length === 0) {
      console.log("No news items to upload.");
      return { taskUid: null, message: "No news items provided." };
    }

    const transformedNews = newsItems.map((item) => ({
      id: item.id,
      title: item.title,
      slug: item.slug,
      seo: item.seo,
      image_featured: item.image_featured,
      keywords: item.keywords,
    }));

    console.log(`Uploading ${transformedNews.length} news items to the 'news' index.`);

    return await newsIndex.addDocuments(transformedNews);
  } catch (error) {
    console.error("Error uploading news to Meilisearch:", error);
    throw error;
  }
};

const uploadSlots = async (slots: SlotItem[]): Promise<EnqueuedTask | { taskUid: null; message: string }> => {
  try {
    if (!slots || slots.length === 0) {
      console.log("No slots to upload.");
      return { taskUid: null, message: "No slots provided." };
    }

    const transformedSlots = slots.map((item) => ({
      id: item.id,
      title: item.title,
      slug: item.slug,
      seo: item.seo,
      image_featured: item.image_featured,
    }));

    console.log(`Uploading ${transformedSlots.length} slots to the 'slots' index.`);

    return await slotsIndex.addDocuments(transformedSlots);
  } catch (error) {
    console.error("Error uploading slots to Meilisearch:", error);
    throw error;
  }
};

const getStats = async (): Promise<MeiliStats> => {
  try {
    // Cast to `any` to bypass the incorrect library types.
    const stats = (await client.getStats()) as any;
    return stats as MeiliStats;
  } catch (error) {
    console.error("Error fetching Meilisearch stats:", error);
    throw error;
  }
};

const getIndexSettings = async (indexUid: string) => {
  try {
    const index = client.index(indexUid);
    return await index.getSettings();
  } catch (error) {
    console.error(`Error fetching settings for index ${indexUid}:`, error);
    throw error;
  }
};

const updateIndexSettings = async (indexUid: string, settings: Settings) => {
  try {
    const index = client.index(indexUid);
    return await index.updateSettings(settings);
  } catch (error) {
    console.error(`Error updating settings for index ${indexUid}:`, error);
    throw error;
  }
};

const getIndexSynonyms = async (indexUid: string) => {
  try {
    const index = client.index(indexUid);
    return await index.getSynonyms();
  } catch (error) {
    console.error(`Error fetching synonyms for index ${indexUid}:`, error);
    throw error;
  }
};

const updateIndexSynonyms = async (indexUid: string, synonyms: Synonyms) => {
  try {
    const index = client.index(indexUid);
    return await index.updateSynonyms(synonyms);
  } catch (error) {
    console.error(`Error updating synonyms for index ${indexUid}:`, error);
    throw error;
  }
};

const resetIndexSynonyms = async (indexUid: string) => {
  try {
    const index = client.index(indexUid);
    return await index.resetSynonyms();
  } catch (error) {
    console.error(`Error resetting synonyms for index ${indexUid}:`, error);
    throw error;
  }
};

export {
  client,
  searchClient,
  operatorsIndex,
  newsIndex,
  slotsIndex,
  uploadOperators,
  searchOperators,
  deleteOperator,
  updateOperator,
  uploadNews,
  uploadSlots,
  getStats,
  getIndexSettings,
  updateIndexSettings,
  getIndexSynonyms,
  updateIndexSynonyms,
  resetIndexSynonyms,
  type Operator,
  type NewsItem,
  type SlotItem,
  type SearchOptions,
  type Task,
  type EnqueuedTask,
};
